"use client";

import { useState, useEffect } from "react";
import { Menu, Facebook, Phone, Mail, Instagram, Youtube, Smartphone, Music as TikTok, MessageCircle, X } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i - 1} className="h-4 w-16 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex font-display">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-xs font-bold uppercase tracking-[0.15em] text-foreground transition-all duration-[var(--duration-fast)] hover:text-primary group py-2">
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-[var(--duration-fast)] group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems
  } = useMenu();
  return <nav className="flex flex-col gap-2 mt-8 font-display">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xl font-bold text-white hover:text-primary border-b border-white/10 pb-4 transition-colors duration-[var(--duration-fast)] uppercase tracking-wider">
          {item.label}
        </Link>)}
      <div className="mt-8 flex flex-col gap-3">
        <Button asChild variant="outline" className="w-full justify-start gap-4 py-6 border-primary/30 text-white hover:bg-primary hover:text-black rounded-sm transition-all">
          <Link to="tel:323-492-6292">
            <Phone className="size-5 text-primary group-hover:text-black" />
            <div className="flex flex-col items-start">
              <span className="text-[10px] uppercase tracking-widest opacity-60">Call Now</span>
              <span className="font-bold text-sm">323-492-6292</span>
            </div>
          </Link>
        </Button>
        <Button asChild variant="outline" className="w-full justify-start gap-4 py-6 border-primary/30 text-white hover:bg-primary hover:text-black rounded-sm transition-all">
          <Link to="sms:323-492-6292">
            <Smartphone className="size-5 text-primary group-hover:text-black" />
            <div className="flex flex-col items-start">
              <span className="text-[10px] uppercase tracking-widest opacity-60">Text Us</span>
              <span className="font-bold text-sm">323-492-6292</span>
            </div>
          </Link>
        </Button>
        <Button asChild variant="outline" className="w-full justify-start gap-4 py-6 border-primary/30 text-white hover:bg-primary hover:text-black rounded-sm transition-all">
          <Link to="mailto:phoenixenerprises@gmail.com">
            <Mail className="size-5 text-primary group-hover:text-black" />
            <div className="flex flex-col items-start">
              <span className="text-[10px] uppercase tracking-widest opacity-60">Email Us</span>
              <span className="font-bold text-sm truncate max-w-[200px]">phoenixenerprises@gmail.com</span>
            </div>
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleFormSubmit = (event: SubmitEvent) => {
      const form = event.target as HTMLFormElement;
      const formIdentifier = (form.id || form.name || form.className || "").toLowerCase();
      if (formIdentifier.includes("booking") || formIdentifier.includes("reservation") || formIdentifier.includes("quote")) {
        if (window.gtag) {
          window.gtag('event', 'generate_lead');
        }
      }
    };
    document.addEventListener("submit", handleFormSubmit, {
      capture: true
    });
    return () => document.removeEventListener("submit", handleFormSubmit, {
      capture: true
    });
  }, []);
  useEffect(() => {
    const script1 = document.createElement("script");
    script1.async = true;
    script1.src = "https://www.googletagmanager.com/gtag/js?id=G-VPC1CFW7YY";
    document.head.appendChild(script1);
    const script2 = document.createElement("script");
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-VPC1CFW7YY');
      gtag('event', 'conversion', {'send_to': 'AW-11528447310/f_Y_CJ_Y_p0ZEM7_9-ko'});
    `;
    document.head.appendChild(script2);
    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-[var(--duration-fast)] ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-medium' : 'bg-background'}`}>
      <div className={`bg-[#1A1A1A] text-white py-2.5 transition-all duration-[var(--duration-fast)] ${isScrolled ? 'hidden' : 'block'}`}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-8">
            <Link to="tel:323-492-6292" className="hover:text-primary transition-colors flex items-center gap-2.5 text-[10px] font-bold tracking-[0.2em] uppercase">
              <Phone className="size-3.5 text-primary" /> 323-492-6292
            </Link>
            <Link to="https://wa.me/3234926292" className="hover:text-primary transition-colors flex items-center gap-2.5 text-[10px] font-bold tracking-[0.2em] uppercase">
              <MessageCircle className="size-3.5 text-primary" /> WhatsApp
            </Link>
            <Link to="sms:323-492-6292" className="hidden sm:flex hover:text-primary transition-colors items-center gap-2.5 text-[10px] font-bold tracking-[0.2em] uppercase">
              <Smartphone className="size-3.5 text-primary" /> Text Us
            </Link>
          </div>
          <div className="flex items-center gap-5">
            <Link to="https://www.facebook.com/reel/1326795919211492" newTab className="hover:text-primary transition-colors"><Facebook className="size-4" /></Link>
            <Link to="https://www.instagram.com/phoenixlimousineservice/" newTab className="hover:text-primary transition-colors"><Instagram className="size-4" /></Link>
            <Link to="https://www.tiktok.com/@phoenix.limousine?_r=1&_t=ZT-97DX9DdCCRZ" newTab className="hover:text-primary transition-colors"><TikTok className="size-4" /></Link>
            <Link to="#" className="hover:text-primary transition-colors"><Youtube className="size-4" /></Link>
          </div>
        </div>
      </div>

      <div className={`border-b border-muted transition-all duration-[var(--duration-fast)] ${isScrolled ? 'py-3' : 'py-6'}`}>
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex-shrink-0">
            <WvcLogo className={`${isScrolled ? 'h-7' : 'h-9 md:h-11'} w-auto transition-all duration-[var(--duration-fast)]`} />
          </Link>

          <div className="hidden lg:flex items-center gap-12">
            <MenuProvider menu_id="45">
              <HeaderMenuConsumer />
            </MenuProvider>
            
            <div className="flex items-center gap-8 pl-8 border-l border-muted">
              <div className="flex items-center gap-2 mr-4">
                <Button asChild variant="outline" size="sm" className="size-9 p-0 border-primary/50 text-primary hover:bg-primary hover:text-black rounded-sm transition-all">
                  <Link to="tel:323-492-6292" title="Call Us"><Phone className="size-4" /></Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="size-9 p-0 border-primary/50 text-primary hover:bg-primary hover:text-black rounded-sm transition-all">
                  <Link to="sms:323-492-6292" title="Text Us"><Smartphone className="size-4" /></Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="size-9 p-0 border-primary/50 text-primary hover:bg-primary hover:text-black rounded-sm transition-all">
                  <Link to="mailto:phoenixenerprises@gmail.com" title="Email Us"><Mail className="size-4" /></Link>
                </Button>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[9px] uppercase tracking-[0.3em] text-muted-foreground font-bold mb-0.5">VIP Concierge</span>
                <Link to="tel:323-492-6292" className="text-lg font-bold text-foreground hover:text-primary transition-colors font-display">
                  323-492-6292
                </Link>
              </div>
              <Button asChild className="bg-linear-to-br from-[#C5A059] to-[#B8935A] hover:from-[#B8935A] hover:to-[#A57D4C] text-black font-bold uppercase tracking-[0.15em] text-[11px] px-8 py-6 rounded-sm shadow-medium transition-all hover:scale-[1.02] active:scale-100 border-none">
                <Link to="/ReservationsPage">Book Now</Link>
              </Button>
            </div>
          </div>

          <div className="lg:hidden flex items-center gap-4">
            <Link to="https://wa.me/3234926292" className="p-2.5 rounded-sm bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all">
              <MessageCircle className="size-5" />
            </Link>
            <Link to="tel:323-492-6292" className="p-2.5 rounded-sm bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all">
              <Phone className="size-5" />
            </Link>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:text-primary transition-colors">
                  <Menu className="size-8" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-[400px] bg-[#1A1A1A] border-l-primary/20 p-0">
                <div className="flex flex-col h-full p-8">
                  <SheetHeader className="p-0 mb-10">
                    <div className="flex items-center justify-between">
                      <SheetTitle className="text-left">
                        <Link to="/">
                          <WvcLogo className="h-8 w-auto brightness-0 invert" />
                        </Link>
                      </SheetTitle>
                    </div>
                  </SheetHeader>
                  
                  <div className="flex-1 overflow-y-auto">
                    <MenuProvider menu_id="45">
                      <MobileMenuConsumer />
                    </MenuProvider>
                  </div>

                  <div className="mt-auto pt-8 border-t border-white/10">
                    <Button asChild className="w-full bg-linear-to-br from-[#C5A059] to-[#B8935A] text-black font-bold uppercase tracking-[0.2em] rounded-sm py-7 shadow-medium text-xs border-none">
                      <Link to="/ReservationsPage">Instant Quote</Link>
                    </Button>
                    <p className="text-center text-white/40 text-[10px] mt-6 uppercase tracking-widest">
                      Professional Chauffeur Service
                    </p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>;
}